import { z } from "zod";
import { OpaqueIdSchema } from "./schemas";

export const COMMERCIAL_TERM_GROUPS = {
  fixed: "One-time costs",
  monthly: "Monthly subscriptions",
  annual: "Annual subscriptions (paid upfront)",
  terms: "Additional terms",
} as const;
export const CommercialTermCategorySchema = z.enum(["fixed", "monthly", "annual", "terms"]);
export const CommercialTermSchema = z
  .strictObject({
    id: OpaqueIdSchema,
    label: z.string().trim().min(1).max(200),
    value: z.string().trim().min(1).max(1000),
    category: CommercialTermCategorySchema.optional(),
    discount: z.boolean().optional(),
    duration: z.string().trim().max(300).optional(),
  })
  .refine(
    (row) =>
      !row.discount ||
      row.category === "fixed" ||
      row.category === "monthly" ||
      row.category === "annual",
    {
      message: "Place discounts in one-time costs, monthly subscriptions, or annual subscriptions.",
      path: ["category"],
    },
  )
  .refine(
    (row) =>
      (row.category !== "monthly" && row.category !== "annual") ||
      !row.discount ||
      Boolean(row.duration),
    {
      message:
        "Enter the subscription discount period, such as First 3 months, First year, or Ongoing.",
      path: ["duration"],
    },
  );
export type CommercialTerm = z.infer<typeof CommercialTermSchema>;
export type CommercialTermCategory = z.infer<typeof CommercialTermCategorySchema>;

// Older saved snapshots remain unchanged; only their display grouping is inferred.
export function commercialTermCategory(row: CommercialTerm): CommercialTermCategory {
  if (row.category) return row.category;
  if (/subscription|monthly|recurring/i.test(row.label)) return "monthly";
  if (/fee|credit|discount|amount due|one.time|setup/i.test(row.label)) return "fixed";
  return "terms";
}
export function commercialTermValue(row: CommercialTerm) {
  let value = row.value;
  if (row.discount && value.trim()) {
    value =
      commercialTermCategory(row) === "fixed"
        ? `-${value.trim().replace(/^[−+-]\s*/, "")}`
        : `${value} off`;
  }
  return `${value}${row.duration ? ` · ${row.duration}` : ""}`;
}
export function commercialTermGroups(rows: CommercialTerm[]) {
  return Object.entries(COMMERCIAL_TERM_GROUPS).map(([category, title]) => ({
    category: CommercialTermCategorySchema.parse(category),
    title,
    rows: rows.filter((row) => commercialTermCategory(row) === category),
  }));
}
